<template>
  <div class="puppetron">
    <div class="page-header">
      <router-link to="/br-guides" class="back-link">← Back</router-link>
      <h1>BR REST API</h1>
      <p class="page-desc">REST API 为常见的浏览器操作提供端点，例如截图、提取 HTML、生成 PDF 等。</p>
    </div>

    <form ref="formtron" id="formtron" @submit.prevent="onSubmit">
      <input
        type="url"
        name="url"
        v-model="url"
        required
        placeholder="Enter a URL"
        @paste="onPaste"
        @focus="onFocus"
        @blur="onBlur"
      />
      <br />

      <!-- Buttons for all supported backend actions -->
      <div class="actions-grid">
        <button
          v-for="act in actions"
          :key="act"
          type="submit"
          :name="act"
          @click="setAction(act)"
          :class="{ active: action === act }"
        >
          {{ labelFor(act) }}
        </button>
      </div>

      <!-- Scrape selectors textarea (only visible when scrape is selected) -->
      <div v-if="action === 'scrape'" class="scrape-controls">
        <label for="selectors"><strong>Scrape 标签选择器 (每行一个)</strong></label>
        <textarea
          id="selectors"
          v-model="selectors"
          placeholder="输入 CSS 选择器, 每行一个. 如:h1 ｜ a ｜ div | .article | .title 等"
          rows="6"
        ></textarea>
        <p class="hint">如果留空，则只会使用 URL 调用抓取程序.</p>
      </div>
    </form>

    <!-- 简单的错误 / 状态显示（保持原有 UX） -->
    <div v-if="statusMessage" class="status-message">{{ statusMessage }}</div>

    <div class="api-reference">
      <div class="api-ref-title">Available endpoints</div>
      <div class="api-ref-grid">
        <div class="api-ref-item" v-for="item in apiDocs" :key="item.path">
          <code class="api-path">{{ item.path }}</code>
          <span class="api-label">{{ item.label }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/*
  修改要点（已在代码中实现）：
  - 使用智能 buildApiUrl 拼接 VUE_APP_API_BASE 与请求路径（自动去重 /api，支持 env 带或不带 /api）
  - 强制前端请求使用 /api/<action>?... （worker 通常期待 /api 前缀）
  - 在发起请求前打印 endpoint（便于 debug）
  - 对不同 Content-Type 做更智能的处理：image/pdf -> blob -> open/download；json -> 智能解析（links/json/data URI）；html/text -> 新窗口展示
  - 保留并尽量不修改你原有的注释与逻辑
*/

export default {
  name: 'ContentGenPuppetron',

  data() {
    return {
      url: '',
      action: 'content',
      protocol: 'http://',
      currentObjectUrl: null,
      statusMessage: '',

      actions: [
        'content',
        'snapshot',
        'screenshot',
        'pdf',
        'scrape',
        'json',
        'links',
        'markdown'
      ],

      selectors: '',

      apiDocs: [
        { path: '/content',    label: 'Fetch HTML content' },
        { path: '/screenshot', label: 'Capture screenshot' },
        { path: '/pdf',        label: 'Render PDF' },
        { path: '/snapshot',   label: 'Webpage snapshot' },
        { path: '/scrape',     label: 'Scrape HTML elements' },
        { path: '/json',       label: 'AI structured data' },
        { path: '/links',      label: 'Retrieve all links' },
        { path: '/markdown',   label: 'Extract Markdown' }
      ]
    };
  },
  methods: {
    encode(u) {
      return encodeURIComponent(u);
    },

    setAction(act) {
      // ensure we only set allowed actions used by backend
      if (this.actions.includes(act)) {
        this.action = act;
      } else {
        this.action = 'content';
      }
    },

    labelFor(act) {
      // Human-friendly labels (you can customize)
      const map = {
        content: 'Content',
        snapshot: 'Snapshot',
        screenshot: 'Screenshot',
        pdf: 'PDF',
        scrape: 'Scrape',
        json: 'JSON',
        links: 'Links',
        markdown: 'Markdown'
      };
      return map[act] || act;
    },

    /*
      更健壮的 buildApiUrl：
      - 处理 baseEnv 带或不带 /api 的情况，避免出现 /api/api/...
      - 确保最终以 base + path 的形式返回（path 以 / 开头）
      - 若 baseEnv 为空，返回相对 path（用于同域且由 Worker Route 绑定的情况）
    */
    buildApiUrl(path) {
      const baseEnv = (process.env.VUE_APP_API_BASE || '').trim();
      const base = baseEnv.replace(/\/+$/, '');
      let p = path || '';
      if (!p.startsWith('/')) p = '/' + p;

      if (!base) return p;

      // 如果 base 以 '/api' 结尾并且 path 也以 '/api' 开头，移除 path 的 '/api' 前缀
      if (base.toLowerCase().endsWith('/api') && p.toLowerCase().startsWith('/api')) {
        p = p.replace(/^\/api/i, '');
      }
      return base + p;
    },

    // 把 data URI (data:...;base64,...) 转成 Blob
    dataUriToBlob(dataUri) {
      const m = dataUri.match(/^data:([^;]+);base64,(.*)$/s);
      if (!m) return null;
      const mime = m[1];
      const b64 = m[2];
      const bin = atob(b64);
      const len = bin.length;
      const u8 = new Uint8Array(len);
      for (let i = 0; i < len; i++) u8[i] = bin.charCodeAt(i);
      return new Blob([u8], { type: mime });
    },

    // 将 blob 处理为 objectURL 并预览 / 下载
    applyPreviewFromBlob(blob, kind) {
      // Revoke previous object URL if present
      if (this.currentObjectUrl) {
        try { URL.revokeObjectURL(this.currentObjectUrl); } catch (e) { /* noop */ }
        this.currentObjectUrl = null;
      }

      const objectUrl = URL.createObjectURL(blob);
      this.currentObjectUrl = objectUrl;

      // Use 'kind' to decide how to present the blob
      if (kind === 'image' || kind === 'pdf') {
        // For images and PDFs, open in a new tab for quick preview
        window.open(objectUrl, '_blank');
        return;
      }

      // For binary or unknown types, trigger download with suggested filename
      const ext = kind === 'pdf' ? 'pdf' : (kind === 'image' ? 'png' : 'bin');
      const filename = this.suggestFilename(ext);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    },

    // 在新窗口展示 HTML 内容（安全性：仅用于调试/preview，不做消毒）
    showHtmlPreview(html) {
      const w = window.open('', '_blank');
      if (w) {
        w.document.open();
        w.document.write(html);
        w.document.close();
      } else {
        alert('Preview blocked by browser. You can configure to allow popups for preview.');
      }
    },

    onPaste(e) {
      const text = e.clipboardData.getData('text') || '';
      if (text.startsWith('http://') || text.startsWith('https://')) {
        e.preventDefault();
        this.url = text;
      }
    },

    onFocus() {
      if (this.url.trim() === '') {
        this.url = this.protocol;
      }
    },

    onBlur() {
      if (this.url.trim() === this.protocol) {
        this.url = '';
      }
    },

    suggestFilename(ext) {
      try {
        const u = new URL(this.url);
        const re = new RegExp('[\\\\/\\?=&:\\s]+', 'g');
        const sanitize = (s) => s.replace(re, '_').replace(/^_+|_+$/g, '');
        const hostPart = sanitize(u.hostname || 'site');
        const pathPart = sanitize(u.pathname || '');
        const name = pathPart ? `${hostPart}_${pathPart}` : hostPart;
        return `capture_${name}.${ext}`;
      } catch {
        return `capture.${ext}`;
      }
    },

    escapeHtml(s) {
      return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    },

    /*
      Main submit handler
      - 构造 /api/<action>?... 路径（Worker 通常期望 /api 前缀）
      - 使用 buildApiUrl 智能拼接（支持 VUE_APP_API_BASE 带或不带 /api）
      - 对不同返回类型进行智能处理
    */
    async onSubmit() {
      this.statusMessage = '';
      if (!this.url || !(this.url.startsWith('http://') || this.url.startsWith('https://'))) {
        alert('Please enter a valid URL including http:// or https://');
        return;
      }

      const params = new URLSearchParams({ url: this.url });

      // include viewport params only for endpoints that normally use them
      if (this.action === 'screenshot' || this.action === 'snapshot') {
        params.set('width', String(window.innerWidth));
        params.set('height', String(window.innerHeight));
      }

      // scrape: if selectors provided, encode as elements JSON array per Cloudflare example
      if (this.action === 'scrape') {
        const lines = (this.selectors || '').split(/\r?\n/).map(s => s.trim()).filter(Boolean);
        if (lines.length) {
          const elements = lines.map(s => ({ selector: s }));
          // JSON.stringify will be percent-encoded by URLSearchParams when setting as value
          params.set('elements', JSON.stringify(elements));
        }
      }

      // Important: include /api prefix in path (worker expects /api/*)
      const endpointPath = `/api/${this.action}?${params.toString()}`;
      const endpoint = this.buildApiUrl(endpointPath);

      // debug: print actual endpoint that will be fetched
      // 这行在调试时很有帮助：确认前端到底请求哪个域名/路径
      console.log('[ContentGen] FETCH endpoint:', endpoint);

      // clear previous object URL if any
      if (this.currentObjectUrl) {
        try { URL.revokeObjectURL(this.currentObjectUrl); } catch (e) { /* noop */ }
        this.currentObjectUrl = null;
      }

      try {
        this.statusMessage = '请求中…';
        // 若需要携带 cookie/session，请把下面的 credentials:'include' 取消注释
        const resp = await fetch(endpoint, { method: 'GET' /* , credentials: 'include' */ });

        if (!resp.ok) {
          // 尝试读取文本用于调试
          const txt = await resp.text().catch(() => '');
          // 如果是 404 且返回 JSON {"error":"not_found"}, 很可能是 worker 路由或 action 名称不匹配
          console.error('[ContentGen] fetch error response:', resp.status, txt);
          throw new Error(`Server returned ${resp.status}: ${txt}`);
        }

        const ct = (resp.headers.get('content-type') || '').toLowerCase();

        // 支持多种 action 返回类型：图片/PDF/HTML/JSON/二进制
        if (ct.includes('image/')) {
          const blob = await resp.blob();
          this.applyPreviewFromBlob(blob, 'image');
          this.statusMessage = '';
        } else if (ct.includes('application/pdf')) {
          const blob = await resp.blob();
          this.applyPreviewFromBlob(blob, 'pdf');
          this.statusMessage = '';
        } else if (ct.includes('application/json')) {
          // JSON 可能是结构化结果（例如 json/action 或 ai result）
          const text = await resp.text();
          try {
            const parsed = JSON.parse(text);

            // 如果是 links action，优先处理为可点击链接列表
            if (this.action === 'links') {
              // 支持直接返回数组或 { links: [...] }
              let links = null;
              if (Array.isArray(parsed)) links = parsed;
              else if (parsed && Array.isArray(parsed.links)) links = parsed.links;

              if (links) {
                // 生成简单 HTML 列表在新窗口展示
                const html = `<h3>Links (${links.length})</h3><ul>${links.map(l => `<li><a href="${this.escapeAttr(l)}" target="_blank" rel="noopener noreferrer">${this.escapeHtml(l)}</a></li>`).join('')}</ul>`;
                this.showHtmlPreview(html);
                this.statusMessage = '';
                return;
              }
            }

            // 如果 JSON 中包含 data: URI（如图片），解析并展示
            if (parsed && typeof parsed.result === 'string' && parsed.result.startsWith('data:')) {
              const blob = this.dataUriToBlob(parsed.result);
              if (blob) {
                const kind = (blob.type || '').startsWith('image/') ? 'image' : 'binary';
                this.applyPreviewFromBlob(blob, kind);
                this.statusMessage = '';
                return;
              }
            }

            // 默认：把 JSON 格式化后在新窗口显示
            this.showHtmlPreview(`<pre>${this.escapeHtml(JSON.stringify(parsed, null, 2))}</pre>`);
            this.statusMessage = '';
          } catch (e) {
            // 非标准 JSON，直接展示文本
            this.showHtmlPreview(`<pre>${this.escapeHtml(text)}</pre>`);
            this.statusMessage = '';
          }
        } else if (ct.includes('text/html')) {
          const text = await resp.text();
          // content / snapshot 通常返回 HTML，直接展示
          this.showHtmlPreview(text);
          this.statusMessage = '';
        } else if (ct.includes('text/markdown') || this.action === 'markdown') {
          // markdown: treat as plain text if server returns markdown or if action is markdown
          const text = await resp.text();
          // 简易渲染：显示原始 markdown（你可替换成 marked + DOMPurify 更安全）
          this.showHtmlPreview(`<pre>${this.escapeHtml(text)}</pre>`);
          this.statusMessage = '';
        } else if (ct.includes('text/plain')) {
          const text = await resp.text();
          this.showHtmlPreview(`<pre>${this.escapeHtml(text)}</pre>`);
          this.statusMessage = '';
        } else {
          // fallback: treat as binary
          const blob = await resp.blob();
          this.applyPreviewFromBlob(blob, 'binary');
          this.statusMessage = '';
        }
      } catch (err) {
        console.error('[ContentGen] Request failed:', err);
        this.statusMessage = '';
        // 为了兼容你之前的 UX，仍保留 alert 提示
        alert('Request failed: ' + (err.message || err));
      }
    },

    // 简单的 attribute escape（用于插入 href）
    escapeAttr(s) {
      return String(s).replace(/"/g, '&quot;');
    }
  },

  beforeDestroy() {
    if (this.currentObjectUrl) {
      try { URL.revokeObjectURL(this.currentObjectUrl); } catch (e) { /* noop */ }
    }
  },
  beforeUnmount() {
    if (this.currentObjectUrl) {
      try { URL.revokeObjectURL(this.currentObjectUrl); } catch (e) { /* noop */ }
    }
  }
};
</script>

<style scoped>
.puppetron {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* Page header */
.page-header {
  margin-bottom: 1.5rem;
}
.back-link {
  display: inline-block;
  font-size: 13px;
  font-weight: 500;
  color: #6B7280;
  text-decoration: none;
  margin-bottom: 0.75rem;
  transition: color 0.15s;
}
.back-link:hover { color: #F6821F; }

h1 {
  font-size: 2rem;
  font-weight: 700;
  color: #F6821F;
  letter-spacing: -0.03em;
  margin: 0 0 0.4rem;
}
.page-desc {
  font-size: 14px;
  color: #6B7280;
  margin: 0;
  line-height: 1.5;
}

/* Form card */
#formtron {
  background: #FFFFFF;
  border: 1px solid #E4E4E0;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.25rem;
}

/* URL input */
input[type='url'] {
  display: block;
  width: 100%;
  padding: 0.6rem 0.9rem;
  border: 1px solid #D4D4D0;
  border-radius: 8px;
  background: #FAFAFA;
  font-size: 14px;
  font-family: inherit;
  color: #1D1D1D;
  transition: border-color 0.15s, box-shadow 0.15s;
}
input[type='url']:focus {
  outline: none;
  border-color: #F6821F;
  box-shadow: 0 0 0 3px rgba(246,130,31,0.15);
}
input[type='url']::placeholder { color: #9CA3AF; }

/* Actions grid */
.actions-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  justify-content: flex-start;
  margin-top: 1rem;
}
.actions-grid button {
  border-radius: 8px;
  border: 1px solid #E4E4E0;
  padding: 0.5rem 1.1rem;
  color: #374151;
  background: #FFFFFF;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  font-family: inherit;
  transition: all 0.15s;
}
.actions-grid button:hover {
  border-color: #F6821F;
  color: #F6821F;
  background: #FFF8F3;
}
.actions-grid button.active {
  border-color: #F6821F;
  color: #FFFFFF;
  background: #F6821F;
  box-shadow: 0 2px 8px rgba(246,130,31,0.25);
}

/* Scrape controls */
.scrape-controls {
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.scrape-controls label { font-size: 13px; font-weight: 500; color: #374151; }
.scrape-controls textarea {
  width: 100%;
  min-height: 96px;
  padding: 0.6rem;
  font-size: 13px;
  font-family: 'ui-monospace', monospace;
  border: 1px solid #D4D4D0;
  border-radius: 8px;
  background: #FAFAFA;
  color: #1D1D1D;
  resize: vertical;
}
.scrape-controls textarea:focus {
  outline: none;
  border-color: #F6821F;
  box-shadow: 0 0 0 3px rgba(246,130,31,0.15);
}
.scrape-controls .hint { font-size: 12px; color: #9CA3AF; }

/* status message */
.status-message {
  margin: 0.75rem 0;
  font-size: 14px;
  color: #6B7280;
  background: #F5F4F2;
  border: 1px solid #E4E4E0;
  border-radius: 8px;
  padding: 0.6rem 0.9rem;
}

/* API reference */
.api-reference {
  background: #FFFFFF;
  border: 1px solid #E4E4E0;
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  margin-top: 1rem;
}
.api-ref-title {
  font-size: 13px;
  font-weight: 600;
  color: #6B7280;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 0.75rem;
}
.api-ref-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0.5rem;
}
.api-ref-item {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.45rem 0;
}
.api-path {
  font-family: 'ui-monospace', monospace;
  font-size: 12px;
  color: #F6821F;
  background: #FFF3E8;
  padding: 0.15rem 0.45rem;
  border-radius: 4px;
  font-weight: 600;
  white-space: nowrap;
}
.api-label { font-size: 13px; color: #6B7280; }

@media (max-width: 580px) {
  h1 { font-size: 1.6rem; }
  .actions-grid button { font-size: 13px; padding: 0.45rem 0.8rem; }
}
</style>